
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { JotformSubmission } from '../types';
import { useJotformSubmissions } from './useJotformSubmissions';
import { useJotformConfig } from '@/hooks/useJotformConfig';
import { toast } from 'sonner';

interface UseReportsProps {
  teamId?: string;
  jotformIds?: string[];
  selectedFormId?: string;
}

export const useReports = ({ teamId, jotformIds = [], selectedFormId = 'all' }: UseReportsProps) => {
  const [reports, setReports] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { config } = useJotformConfig();
  
  // Use config.form_ids as fallback if jotformIds is empty
  const formIdsToUse = jotformIds.length > 0 ? jotformIds : (config?.form_ids || []);
  
  const { 
    submissions, 
    isLoading: isLoadingSubmissions, 
    refetch: refetchSubmissions,
    setApiKey
  } = useJotformSubmissions({ 
    formIds: formIdsToUse,
    teamId,
    autoFetch: false, // We'll manually trigger fetch when config is ready
    selectedFormId
  });

  // Set API key from config when available
  useEffect(() => {
    if (config?.api_key) {
      try {
        setApiKey(config.api_key);
        // Only refetch if a specific form is selected
        if (selectedFormId !== 'all') {
          refetchSubmissions();
        }
      } catch (error) {
        console.error('Error setting API key:', error);
        toast.error('Failed to set Jotform API key');
      }
    }
  }, [config, setApiKey, refetchSubmissions, selectedFormId]);
  
  // Fetch reports from Supabase
  const fetchReports = useCallback(async () => {
    if (!teamId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get reports
      const { data: reportsData, error: reportsError } = await supabase
        .from('reports')
        .select('*')
        .eq('team_id', teamId)
        .order('submitted_at', { ascending: false });
        
      if (reportsError) throw reportsError;
      
      setReports(reportsData || []);
      
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      toast.error(`Failed to fetch reports: ${error.message || 'Unknown error'}`);
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);
  
  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);
  
  // Combined loading state
  const combinedIsLoading = isLoading || isLoadingSubmissions;
  
  return { 
    reports, 
    submissions, 
    isLoading: combinedIsLoading,
    refetchReports: fetchReports,
    setJotformApiKey: setApiKey
  };
};
